"use client";

import type { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { useTenant } from "@tillix/tenants/react";
import { baseTooltipKit } from "./tooltip";
import { conEdisonTooltipKit } from "./tooltip.con-edison";
import type { OptionalTenantProp } from "../../../types";

export interface ToolTipParts {
  Provider: React.FC<TooltipPrimitive.Provider.Props>;
  Root: React.FC<TooltipPrimitive.Root.Props>;
  Trigger: React.FC<TooltipPrimitive.Trigger.Props>;
  Popup: React.FC<TooltipPrimitive.Popup.Props>;
}

function useTooltipKit(tenantProp?: OptionalTenantProp["tenant"]) {
  const contextTenant = useTenant();
  const tenantRefId = tenantProp?.id ?? contextTenant?.id;

  switch (tenantRefId) {
    case "con-edison":
      return conEdisonTooltipKit;
    default:
      return baseTooltipKit;
  }
}

/**
 * Factory that returns a component which:
 * 1) picks the right tenant kit
 * 2) renders the correct part
 */
function makeTooltipPart<P extends object>(
  pick: (kit: ToolTipParts) => React.FC<P>,
) {
  return function TooltipPart(props: P & OptionalTenantProp) {
    const { tenant, ...rest } = props;
    const Kit = useTooltipKit(tenant);
    const ComponentPart = pick(Kit);
    return <ComponentPart {...(rest as P)} />;
  };
}

export const Tooltip = makeTooltipPart<TooltipPrimitive.Root.Props>(
  (k) => k.Root,
) as React.FC<TooltipPrimitive.Root.Props & OptionalTenantProp> & {
  Provider: React.FC<TooltipPrimitive.Provider.Props & OptionalTenantProp>;
  Root: React.FC<TooltipPrimitive.Root.Props & OptionalTenantProp>;
  Trigger: React.FC<TooltipPrimitive.Trigger.Props & OptionalTenantProp>;
  Popup: React.FC<TooltipPrimitive.Popup.Props & OptionalTenantProp>;
};

Tooltip.Provider = makeTooltipPart<TooltipPrimitive.Provider.Props>(
  (k) => k.Provider,
);
Tooltip.Root = makeTooltipPart<TooltipPrimitive.Root.Props>((k) => k.Root);
Tooltip.Trigger = makeTooltipPart<TooltipPrimitive.Trigger.Props>(
  (k) => k.Trigger,
);
Tooltip.Popup = makeTooltipPart<TooltipPrimitive.Popup.Props>((k) => k.Popup);
